import { useState, useCallback, useRef, useEffect } from "react";
import { FilterType } from "./reportsUtils";

interface UseSidebarCollapseReturn {
  isCollapsed: boolean;
  toggleCollapse: () => void;
}

// Check if device is mobile
const isMobileDevice = () => {
  return window.innerWidth <= 768;
};

export const useSidebarCollapse = (): UseSidebarCollapseReturn => {
  // Start collapsed on mobile, expanded on desktop
  const [isCollapsed, setIsCollapsed] = useState(isMobileDevice());

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Handle window resize to auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      const isMobile = isMobileDevice();
      // Only auto-collapse when resizing to mobile, don't auto-expand when going to desktop
      if (isMobile && !isCollapsed) {
        // Don't force collapse if user has expanded it
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);

  return { isCollapsed, toggleCollapse };
};

interface UseReportsSearchReturn {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
}

export const useReportsSearch = (): UseReportsSearchReturn => {
  const [searchTerm, setSearchTerm] = useState("");

  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  return { searchTerm, setSearchTerm, clearSearch };
};

interface UseReportsFilterReturn {
  selectedFilter: FilterType;
  setSelectedFilter: (filter: FilterType) => void;
}

export const useReportsFilter = (): UseReportsFilterReturn => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("all");

  return { selectedFilter, setSelectedFilter };
};

interface UseSelectedReportScrollReturn {
  selectedItemRef: React.RefObject<HTMLDivElement | null>;
}

export const useSelectedReportScroll = (
  selectedReportId?: string | null
): UseSelectedReportScrollReturn => {
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Scroll to selected item when selectedReportId changes
  useEffect(() => {
    if (selectedReportId && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedReportId]);

  return { selectedItemRef };
};
